'use client';

import React, { ReactNode } from 'react';
import { logger } from '../lib/logger.js';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  /** Bumped on reset so children remount as a fresh subtree (see reset()). */
  resetCount: number;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, resetCount: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error('Error caught by chat ErrorBoundary', error, {
      componentStack: errorInfo.componentStack,
    });
  }

  // "Try again" must actually recover. Bumping resetCount changes the child
  // subtree's key, forcing a fresh remount rather than re-rendering the same
  // errored instance (which would re-throw immediately for a deterministic bug).
  reset = () => {
    this.setState((prev) => ({ hasError: false, error: null, resetCount: prev.resetCount + 1 }));
  };

  reload = () => {
    if (typeof window !== 'undefined') window.location.reload();
  };

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }

      // Contained fallback (h-full, not h-screen) so the surrounding Hub shell
      // — sidebar/nav — survives a single bad chat render.
      return (
        <div className="flex items-center justify-center h-full w-full p-6 bg-destructive/5">
          <div className="max-w-md w-full bg-card border border-border rounded-lg shadow-lg p-6">
            <h1 className="text-xl font-bold text-destructive mb-3">Something went wrong</h1>
            <p className="text-muted-foreground mb-4 break-words">{this.state.error.message}</p>
            <div className="flex gap-2">
              <button
                onClick={this.reset}
                className="flex-1 min-h-11 px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors"
              >
                Try again
              </button>
              <button
                onClick={this.reload}
                className="flex-1 min-h-11 px-4 py-2 border border-border rounded hover:bg-muted transition-colors"
              >
                Reload page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return <React.Fragment key={this.state.resetCount}>{this.props.children}</React.Fragment>;
  }
}
